/**
 * Per-species foliage-cluster atlas for the procedural trees (issue #58). Each
 * runtime foliage card is a whole leafy cluster at 2 tris that samples ONE tile
 * of this shared atlas; the alpha-tested cutout (transparent gaps between the
 * painted leaf lobes) is what gives the canopy its sky-gaps at 15 m.
 *
 * PRAGMATIC BAKE CHOICE (documented per the issue): the reference
 * (Braffolk/fable5-world-demo, MIT, commit fd75fdb) bakes this atlas by
 * rendering real leaf-spray geometry once with an orthographic camera into a
 * render target. ray2's viewer has no offscreen-render bake step and no
 * Node-side GPU, so standing that up cleanly was out of scope for one unit.
 * Instead the atlas is painted procedurally on a 2D canvas at runtime, reusing
 * the exact deterministic hash01-seeded lobe/gap idiom already shipped in
 * vegetationTexture.ts (issue #57) — a leafy cluster of jittered lobes with
 * visible gaps, not a solid blob. The card GEOMETRY is still baked at build
 * time from the real skeleton anchors; only the cluster TEXTURE it samples is
 * painted here. The atlas layout (this file's constants) is shared by the bake
 * (which assigns card UVs) and the painter, so they can never drift.
 *
 * Pure layout maths are three.js-free and unit-tested in Node; the canvas
 * painter (makeTreeFoliageTexture) no-ops to a transparent canvas when there is
 * no 2D context (the vitest node env / happy-dom), the same contract
 * makeVegetationTexture() uses — it is only exercised for real in the browser.
 */
import { CanvasTexture, ClampToEdgeWrapping, SRGBColorSpace } from 'three';
import { TREE_SPECIES } from './treeSpecies.ts';

/** One atlas column per species; two rows give two cluster variants so a crown
 * reads as differently-toned clumps rather than one repeated stamp. */
export const ATLAS_COLS = TREE_SPECIES.length;
export const ATLAS_ROWS = 2;
export const CELL_PX = 256;

export interface TileUvRect {
  u0: number;
  v0: number;
  u1: number;
  v1: number;
}

/** UV rect (three.js convention, v=0 at bottom) for a (column, row) atlas tile.
 * Row 0 maps to the top canvas row (canvas y grows down, UV v grows up). */
export function atlasTileUv(column: number, row: number): TileUvRect {
  if (column < 0 || column >= ATLAS_COLS) throw new Error(`tree atlas column ${column} out of range`);
  if (row < 0 || row >= ATLAS_ROWS) throw new Error(`tree atlas row ${row} out of range`);
  const u0 = column / ATLAS_COLS;
  const u1 = (column + 1) / ATLAS_COLS;
  const v1 = 1 - row / ATLAS_ROWS;
  const v0 = 1 - (row + 1) / ATLAS_ROWS;
  return { u0, v0, u1, v1 };
}

/** Deterministic fractional hash in [0, 1) (same idiom as vegetationTexture). */
function hash01(i: number): number {
  const x = Math.sin(i * 12.9898) * 43758.5453;
  return x - Math.floor(x);
}

/** A painted foliage lobe in 0..1 cell-fraction space (canvas y grows down). */
export interface ClusterLobe {
  cx: number;
  cy: number;
  r: number;
  fillSeed: number;
}

/**
 * Deterministic scatter of `count` overlapping lobes filling a cluster tile,
 * each radius capped below the mean spacing so gaps stay open between them
 * (the alpha-tested sky-gaps). Pure and three-free; the painter just draws
 * whatever this returns. Broadleaf clusters (isNeedle=false) are a rounded
 * blob of lobes; needle clusters (isNeedle=true) bias into a taller, narrower
 * spray.
 */
export function clusterLobes(seed: number, count: number, isNeedle: boolean): ClusterLobe[] {
  const lobes: ClusterLobe[] = [];
  const spreadX = isNeedle ? 0.28 : 0.36;
  const spreadY = isNeedle ? 0.4 : 0.34;
  for (let i = 0; i < count; i++) {
    const ang = hash01(seed * 3.1 + i * 1.7) * Math.PI * 2;
    const rad = Math.sqrt(hash01(seed * 5.3 + i * 2.9)); // fill the disc, not just centre
    lobes.push({
      cx: 0.5 + Math.cos(ang) * rad * spreadX,
      cy: 0.5 + Math.sin(ang) * rad * spreadY,
      r: (isNeedle ? 0.08 : 0.11) + hash01(seed * 7.7 + i * 4.1) * 0.05,
      fillSeed: seed * 97 + i * 13 + 1,
    });
  }
  return lobes;
}

/** hsl fill for a lobe: green hue/lightness jitter so clusters vary in tone. */
function foliageFill(seed: number, kind: 'conifer' | 'broadleaf'): string {
  const base = kind === 'conifer' ? 96 : 84;
  const hue = base + hash01(seed * 3.1) * 34;
  const sat = 34 + hash01(seed * 5.7) * 28;
  const light = 30 + hash01(seed * 7.3) * 26;
  return `hsl(${hue.toFixed(0)}, ${sat.toFixed(0)}%, ${light.toFixed(0)}%)`;
}

let cached: CanvasTexture | undefined;

/**
 * Builds (or returns the cached) shared foliage atlas texture: ATLAS_COLS
 * species columns x ATLAS_ROWS cluster variants. One shared texture for every
 * tree foliage InstancedMesh (never one per tree — issue #17). No-ops to a
 * transparent canvas when there is no 2D context (node/test).
 */
export function makeTreeFoliageTexture(): CanvasTexture {
  if (cached) return cached;
  const canvas = document.createElement('canvas');
  canvas.width = ATLAS_COLS * CELL_PX;
  canvas.height = ATLAS_ROWS * CELL_PX;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    for (let col = 0; col < ATLAS_COLS; col++) {
      const sp = TREE_SPECIES[col];
      const isNeedle = sp.kind === 'conifer';
      for (let row = 0; row < ATLAS_ROWS; row++) {
        const x0 = col * CELL_PX;
        const y0 = row * CELL_PX;
        const seed = col * 31 + row * 7 + 1;
        const lobes = clusterLobes(seed, isNeedle ? 26 : 20, isNeedle);
        for (const lobe of lobes) {
          ctx.fillStyle = foliageFill(lobe.fillSeed, sp.kind);
          ctx.beginPath();
          ctx.ellipse(
            x0 + lobe.cx * CELL_PX,
            y0 + lobe.cy * CELL_PX,
            lobe.r * CELL_PX,
            lobe.r * CELL_PX * (isNeedle ? 1.25 : 1),
            0,
            0,
            Math.PI * 2,
          );
          ctx.fill();
        }
      }
    }
  }
  const texture = new CanvasTexture(canvas);
  texture.wrapS = ClampToEdgeWrapping;
  texture.wrapT = ClampToEdgeWrapping;
  texture.colorSpace = SRGBColorSpace;
  texture.anisotropy = 4;
  cached = texture;
  return texture;
}
